import { Modal } from '../Modal';
import { PreviewTable } from '../PreviewTable';
import { formatDateTime, toIso } from '../../lib/formatDate';

export interface OtherInfoData {
  createdAt?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  /**
   * The record whose audit entry holds this row's latest update, where that is not the row
   * itself — a row another record's save rewrites is logged under THAT record, so `Latest
   * update` looked under the row's own id and found nothing. Absent means the row's own key.
   */
  auditEntityId?: string | null;
}

/**
 * Read-only audit popover opened from a row's Other Info action — reference §14.
 *
 * This is the ONLY place row-level audit metadata appears in a list. Promoting it to
 * columns would cost width on every screen to answer a question asked once a month.
 */
export function OtherInfoModal({
  open,
  onClose,
  data,
  id,
}: {
  open: boolean;
  onClose: () => void;
  data: OtherInfoData | null;
  id?: string;
}) {
  if (!data) {
    return null;
  }

  return (
    // `sm`: pure information, no fields.
    <Modal open={open} onClose={onClose} title="Other Info" size="sm" id={id ?? 'other-info-modal'}>
      <PreviewTable
        rows={[
          { label: 'Created by', value: data.createdByName?.trim() || undefined },
          { label: 'Created at', value: <Stamp value={data.createdAt} /> },
          /*
            `Updated by` is GONE (product call, 21 Oct 2026). The `Latest update` COLUMN now
            carries the instant over its actor, so repeating the name here printed the same
            value twice on one screen — the rule this product applies everywhere else.

            It stays safe to drop because these columns cannot be hidden: `headerGroups` only
            offers a column that declares a group, and the shell's audit pair declares none.
            `Updated at` stays, because a stamp with no actor still has nowhere else to live
            when a page passes `latestUpdate={false}`.
          */
          { label: 'Updated at', value: <Stamp value={data.updatedAt} /> },
        ]}
      />
    </Modal>
  );
}

function Stamp({ value }: { value?: string | null }) {
  if (!value) {
    return null;
  }

  return (
    <time dateTime={toIso(value)} className="font-mono tabular-nums">
      {formatDateTime(value)}
    </time>
  );
}
